import { useId, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import type { Visibility } from '../query/types'

/** The name being typed survives an accidental close or a reload of the tab (ED-52). */
const DRAFT_KEY = 'vonk.crm.weergave-concept'
const MAX_NAME = 60

function readDraft(): string {
  try {
    return sessionStorage.getItem(DRAFT_KEY) ?? ''
  } catch {
    return '' // storage blocked (private window, site data cleared): no draft, the dialog works
  }
}

function writeDraft(name: string): void {
  try {
    if (name) sessionStorage.setItem(DRAFT_KEY, name)
    else sessionStorage.removeItem(DRAFT_KEY)
  } catch {
    // The draft is a convenience only.
  }
}

const CHOICES: { value: Visibility; label: string }[] = [
  { value: 'prive', label: 'Privé – alleen jij ziet hem' },
  { value: 'team', label: 'Team – iedereen met CRM-toegang' },
]

/**
 * "Weergave opslaan" (design §5.4): a name and who sees the view. `onSave` makes the view; when it
 * is refused (a name that is taken, say) its message shows here and the dialog stays open.
 */
export function SaveViewDialog({
  open,
  onOpenChange,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (input: { name: string; visibility: Visibility }) => Promise<void>
}) {
  const nameId = useId()
  const group = useId()
  const [name, setName] = useState(readDraft)
  const [visibility, setVisibility] = useState<Visibility>('prive')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [wasOpen, setWasOpen] = useState(open)
  // Every opening starts from the draft, private, and without the message of the last attempt.
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setName(readDraft())
      setVisibility('prive')
      setError(null)
    }
  }

  function change(next: string) {
    setName(next)
    writeDraft(next.trim())
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || busy) return
    setBusy(true)
    setError(null)
    try {
      await onSave({ name: trimmed, visibility })
      writeDraft('')
      onOpenChange(false)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Opslaan mislukt.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Weergave opslaan</DialogTitle>
          <DialogDescription>Bewaar de filters, kolommen en sortering van deze lijst. De zoekterm hoort er niet bij.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => void submit(event)} className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={nameId} className="text-sm font-medium">
              Naam
            </label>
            <Input id={nameId} maxLength={MAX_NAME} value={name} onChange={(event) => change(event.target.value)} className="max-md:h-11 max-md:text-base" />
          </div>
          <fieldset className="flex flex-col gap-1">
            <legend className="sr-only">Wie ziet deze weergave?</legend>
            {CHOICES.map((choice) => (
              <label key={choice.value} className="flex min-h-8 cursor-pointer items-center gap-2 text-sm max-md:min-h-11">
                <input
                  type="radio"
                  name={group}
                  className="size-4 shrink-0 accent-primary"
                  checked={visibility === choice.value}
                  onChange={() => setVisibility(choice.value)}
                />
                {choice.label}
              </label>
            ))}
          </fieldset>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                writeDraft('')
                onOpenChange(false)
              }}
            >
              Annuleren
            </Button>
            <Button type="submit" disabled={busy || !name.trim()}>
              Opslaan
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
